import React from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeading } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { COMMUNITY_IMAGE, DAKAR_IMAGE } from "@/lib/constants";
import {
  Target, Eye, Flag, ArrowRight, FileText, Download,
  GraduationCap, Landmark, Sparkles, HeartHandshake, Building2, Scale,
  Clock, HeartPulse, Briefcase, Globe,
} from "lucide-react";

const VALEURS = [
  { Icon: Eye, title: "Notre vision", text: "Bâtir une Nation souveraine, stable, juste et spirituellement consciente, où le progrès matériel et l'élévation morale avancent ensemble. DEFAR affirme que le développement ne peut être importé et que la République n'est efficace que si elle ressemble au peuple dont elle porte les aspirations." },
  { Icon: Target, title: "Notre mission", text: "Être la force motrice d'un renouveau politique : faire émerger un nouveau leadership exemplaire, réhabiliter le sens du devoir, de la justice et du service, et construire un avenir juste, solidaire et durable au service du peuple et des générations à venir." },
  { Icon: Flag, title: "Nos valeurs", text: "Foi, éthique, justice, intégrité, compétence et responsabilité. La Teranga, la fraternité, la solidarité et l'autodétermination, dans le respect de notre diversité ethnique, religieuse et culturelle, richesse fondamentale de notre unité." },
];

const AXES = [
  { Icon: GraduationCap, title: "Éducation", text: "Une priorité stratégique nationale : former l'être humain dans sa globalité, par l'éthique, le savoir et l'enracinement culturel." },
  { Icon: Landmark, title: "République enracinée", text: "Construire une République sénégalaise concrète, rendre opérationnelle la devise « Un Peuple, Un But, Une Foi » et restaurer l'éthique publique." },
  { Icon: Sparkles, title: "La jeunesse", text: "Boussole de la Nation : toute politique publique est une politique de jeunesse, évaluée à l'aune de son impact sur les jeunes." },
  { Icon: HeartHandshake, title: "Liberté de culte", text: "Au-delà d'une laïcité rigide : égalité absolue des convictions et pacte national avec les autorités religieuses." },
  { Icon: Building2, title: "État & territoires", text: "Refonder l'administration territoriale dans une logique d'efficacité et de proximité, et rationaliser l'administration centrale." },
  { Icon: Scale, title: "Démocratie & stabilité", text: "Une démocratie pensée à partir de notre histoire : stabilisatrice, crédible, au service du développement et de la paix sociale." },
  { Icon: Clock, title: "Autodétermination", text: "Maîtriser notre temps politique : refuser les calendriers imposés et définir nos propres priorités." },
  { Icon: HeartPulse, title: "Santé & sport", text: "Une même politique de la vie : prévention, petite enfance et lien structurel entre sport, éducation et santé publique." },
  { Icon: Briefcase, title: "Travail & développement", text: "Un développement enraciné dans les valeurs : le travail comme acte de service, la richesse au service de la dignité humaine." },
  { Icon: Globe, title: "Diplomatie", text: "Une voix libre fondée sur la souveraineté, la paix et la dignité — un pont entre les peuples et les cultures." },
];

const DOCS = [
  { title: "Manifeste DEFAR", desc: "Notre vision pour un Sénégal prospère, ancré dans ses valeurs." },
  { title: "Charte du mouvement", desc: "Les principes fondateurs de DEFAR." },
  { title: "Statuts et règlement", desc: "Organisation et gouvernance interne." },
];

export default function Mouvement() {
  return (
    <div data-testid="mouvement-page">
      <SEO title="Le mouvement" description="Découvrez DEFAR - Les Bâtisseurs : notre vision, notre mission, nos valeurs et les grands axes de notre projet pour un Sénégal souverain et juste." />
      <PageHero
        overline="Le mouvement"
        title="DEFAR - Les Bâtisseurs"
        subtitle="Pour un Sénégal prospère, ancré dans ses valeurs. Un acte d'autodétermination qui propose une voie sénégalaise, enracinée dans notre foi, notre histoire et nos valeurs, tournée vers l'avenir."
        image={DAKAR_IMAGE}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-green">Notre raison d'être</span>
          <h2 className="mt-3 text-3xl font-bold text-slate-900">Un renouveau enraciné dans nos valeurs</h2>
          <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
            <p>
              DEFAR est né de la conviction que les crises traversées par le Sénégal ne sont pas
              uniquement économiques ou institutionnelles : elles sont d'abord morales, spirituelles
              et organisationnelles.
            </p>
            <p>
              DEFAR conçoit l'être humain comme le bâtisseur d'un espace de bien-être social et
              collectif. La politique y devient un art éthique de gouverner, fondé sur la foi, la
              justice, l'intégrité, la compétence et la responsabilité.
            </p>
            <p className="font-medium text-slate-800">
              « DEFAR est un appel à bâtir une Nation souveraine, stable, juste et spirituellement
              consciente, où le progrès matériel et l'élévation morale se renforcent mutuellement. »
            </p>
          </div>
        </div>
        <img src={COMMUNITY_IMAGE} alt="Communauté DEFAR" className="w-full aspect-[4/3] object-cover rounded-sm" />
      </section>

      <section className="bg-brand-surface py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center overline="Nos fondamentaux" title="Vision, mission et valeurs" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {VALEURS.map(({ Icon, title, text }) => (
              <div key={title} className="bg-white border border-border p-8 rounded-sm">
                <div className="h-12 w-12 flex items-center justify-center bg-brand-green text-white">
                  <Icon size={24} />
                </div>
                <h3 className="mt-5 text-xl font-bold text-slate-900">{title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Axes du programme */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <SectionHeading overline="Notre programme" title="Les grands axes du projet" subtitle="Dix engagements pour refonder l'action publique au service du peuple sénégalais." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AXES.map(({ Icon, title, text }) => (
            <div key={title} className="border border-border p-6 rounded-sm hover:shadow-md transition-shadow">
              <div className="h-11 w-11 flex items-center justify-center bg-brand-blue/5 text-brand-blue">
                <Icon size={22} />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Organisation / Bureau (COPIL) */}
      <section className="bg-brand-surface py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Organisation" title="Le bureau du mouvement" subtitle="Le Comité de Pilotage (COPIL) qui anime et coordonne l'action de DEFAR." />
          <Link to="/le-bureau" className="inline-flex items-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-8 py-4 font-semibold">
            Découvrir les membres du bureau <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Documents officiels */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <SectionHeading overline="Ressources" title="Documents officiels" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {DOCS.map((d) => (
            <div key={d.title} className="bg-white border border-border p-6 rounded-sm flex items-start gap-4">
              <div className="h-11 w-11 flex items-center justify-center bg-brand-blue/5 text-brand-blue shrink-0">
                <FileText size={22} />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">{d.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{d.desc}</p>
                <button className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-green">
                  <Download size={14} /> Télécharger (PDF)
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-brand-blue py-16 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white">DEFARISTES du Sénégal et de la diaspora</h2>
          <p className="mt-4 text-blue-100">Rejoignez le mouvement et bâtissons ensemble une Nation souveraine, juste et solidaire.</p>
          <Link to="/adhesion" className="mt-8 inline-flex items-center gap-2 bg-brand-green text-white hover:bg-brand-greenhover transition-colors px-8 py-4 font-semibold">
            Rejoindre DEFAR <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
